import { useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { format, parseISO } from 'date-fns';
import { isUndefined } from 'lodash';

import { amplitudeService, ScreenName } from '../../../../../shared/amplitude';
import { ViewReviewDetail } from '../../../../../shared/amplitude/events/review';
import { QuestionMark } from '../../../../../shared/icons';
import { useAppSelector } from '../../../../../shared/store';
import { useScreenName } from '../../../../../shared/hooks';

import type { ProductReview } from '../../ProductReview.service';
import { getBadgeColors } from '../../utils';
import ReviewModal from '../ReviewModal/ReviewModal';
import ReviewModalImagContent from '../ReviewModal/ReviewModalImagContent';
import ReviewModalPostContent from '../ReviewModal/ReviewModalPostContent';
import ReviewComment from '../../components/ReviewComment';
import HelpfulButton from '../HelpfulButton';
import {
  AboutUser,
  ContentsWrapper,
  Footer,
  ImageWrapper,
  Profile,
  ProfileWrapper,
  ReviewDate,
  UserName,
} from './styled-components';
import COLOR from '../../../../../shared/constant/colorset';
import { multiMaxLineText } from '../../../../../shared/utils';
import { ReviewContentGrid } from '../ReviewModal/styled-components';

const Alert = styled.div`
  position: absolute;
  display: none;
  bottom: -78px;
  padding: 12px;
  width: 317px;
  border: 1px solid ${COLOR.kurlyGray800};
  border-radius: 4px;
  background-color: ${COLOR.kurlyWhite};
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: ${COLOR.kurlyGray450};
`;

const Badge = styled.span<{ colorSet: SerializedStyles }>`
  display: inline-block;
  height: 18px;
  border: 1px solid;
  border-radius: 3px;
  padding: 3px 4px 3px 5px;
  margin-right: 4px;
  font-weight: 500;
  font-size: 10px;
  line-height: 10px;
  text-align: center;
  word-break: keep-all;
  ${(props) => props.colorSet}
`;

const Contents = styled.p`
  padding-top: 12px;
  word-break: break-word;
  white-space: pre-line;
  font-weight: 400;
  font-size: 14px;
  line-height: 19px;
  color: ${COLOR.kurlyGray800};
`;

const DealProductName = styled.h3`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  color: ${COLOR.kurlyGray450};
  ${multiMaxLineText(1)}
`;

const DistinctContentsNotice = styled.div`
  flex-shrink: 0;
  position: relative;
  width: 14px;
  height: 14px;

  :hover div {
    display: block;
  }
`;

const Image = styled.button<{ url: string }>`
  width: 93px;
  height: 93px;
  flex-shrink: 0;
  background: ${(props) => `url(${props.url}) no-repeat`};
  background-size: cover;

  :first-of-type {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  :last-of-type {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const ProductName = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 19px;
  padding-right: 20px;
`;

const View = styled.div`
  display: flex;
  padding: 30px 0 19px 20px;
  border-bottom: 1px solid ${COLOR.bg};
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
`;

interface Props extends ProductReview {
  sortType: 'RECOMMEND' | 'RECENTLY';
}

export default function ReviewRow({ sortType, ...review }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const router = useRouter();

  const productData = useAppSelector(({ productDetail }) => productDetail);
  const { productSites } = productData;
  const isNormalReview = review.type === '일반';

  useScreenName(ScreenName.PRODUCT_REVIEW_SUBTAB);

  const handleClick = async (index: number) => {
    try {
      await amplitudeService.setScreenName(ScreenName.PRODUCT_REVIEW_SUBTAB);
      await amplitudeService.logEvent(
        new ViewReviewDetail({
          reviewData: review,
          sortType,
          productData,
        }),
      );
      await amplitudeService.setScreenName(ScreenName.PRODUCT_REVIEW_DETAIL);
    } finally {
      setSelectedIndex(index);
      setIsOpen(true);
    }
  };

  return (
    <>
      <View>
        <ProfileWrapper>
          <AboutUser>
            {!isNormalReview ? <Badge colorSet={getBadgeColors(review.type)}>{review.type}</Badge> : null}
            {!!review.reviewerGrade ? <Badge colorSet={getBadgeColors()}>{review.reviewerGrade}</Badge> : null}
            <UserName>{review.reviewer}</UserName>
          </AboutUser>
          {productSites?.includes('BEAUTY') && (
            <Profile>
              <span>{review.profiles.beautyProfile || review.profiles.marketProfile}</span>
            </Profile>
          )}
        </ProfileWrapper>
        <ContentsWrapper>
          <div>
            <ProductName>
              <DealProductName>{review.dealProductName}</DealProductName>
              {Number(router.query.productCode) !== review.contentsProductNo && (
                <DistinctContentsNotice>
                  <QuestionMark stroke={COLOR.kurlyGray350} fill={COLOR.kurlyGray350} />
                  <Alert>
                    이 후기는 판매구성이 다르지만 본품이 동일한 상품을 구매 후 작성된 후기입니다. 판매구성에 따라 용량,
                    구성, 상품명 등 일부 정보가 상이할 수 있습니다.
                  </Alert>
                </DistinctContentsNotice>
              )}
            </ProductName>
            <Contents
              css={css`
                padding-right: 20px;
              `}
            >
              {review.contents}
            </Contents>
            {review.images.length > 0 && (
              <ImageWrapper>
                {review.images.map(({ id: imageId, url }, index) => (
                  <Image key={imageId} url={url} onClick={() => handleClick(index)} />
                ))}
              </ImageWrapper>
            )}
            <Footer
              css={css`
                padding-right: 20px;
              `}
            >
              <div>
                {review.registrationDate ? (
                  <ReviewDate>{format(parseISO(review.registrationDate), 'yyyy.MM.dd')}</ReviewDate>
                ) : null}
              </div>
              <HelpfulButton
                reviewId={review.id}
                sortType={sortType}
                count={review.likeCount}
                isChecked={review.hasLiked}
              />
            </Footer>
          </div>
          {!isUndefined(review.comment) && <ReviewComment {...review.comment} />}
        </ContentsWrapper>
      </View>
      <ReviewModal isOpen={isOpen} onDismiss={() => setIsOpen(false)}>
        <ReviewContentGrid>
          <ReviewModalImagContent images={review.images} initialSelectedIndex={selectedIndex} />
          <ReviewModalPostContent {...review} reviewType="POST" sortType={sortType} />
        </ReviewContentGrid>
      </ReviewModal>
    </>
  );
}
